import { SearchOutlined } from '@ant-design/icons';
import { Empty, Input, type InputRef, Modal, theme } from 'antd';
import { useEffect, useId, useRef, useState } from 'react';
import { preloadRoute } from '@/config/preloadRoute';
import { localizeMenuItemName } from '@/config/routeLabels';
import type { AppMenuItem } from '@/config/types';
import { useShellText } from '@/hooks/useShellText';
import { useLocaleStore } from '@/stores/localeStore';
import { searchMenu } from './searchMenu';

export function CommandPalette({
  open,
  isPhone = false,
  items,
  onClose,
  onNavigate,
}: {
  open: boolean;
  isPhone?: boolean;
  items: AppMenuItem[];
  onClose: () => void;
  onNavigate: (path: string, onApproved?: () => void) => void;
}) {
  const t = useShellText();
  const locale = useLocaleStore((state) => state.locale);
  const { token } = theme.useToken();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<InputRef>(null);
  const composing = useRef(false);
  const listId = useId();
  const scrollSelection = useRef(false);
  const pointerPosition = useRef<{ x: number; y: number } | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const results = searchMenu(items, query, locale);
  const activePath = results[active]?.path;
  useEffect(() => {
    if (open && activePath) preloadRoute(activePath);
  }, [open, activePath]);
  useEffect(() => {
    if (open && activePath && scrollSelection.current) {
      list.current
        ?.querySelector<HTMLElement>('[aria-selected="true"]')
        ?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [open, activePath]);
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);
  function choose(index: number) {
    const path = results[index]?.path;
    if (path) {
      onNavigate(path, onClose);
    }
  }
  return (
    <Modal
      open={open}
      title={t('搜索菜单', 'Search menus')}
      footer={
        isPhone ? null : (
          <div
            style={{
              textAlign: 'start',
              color: token.colorTextSecondary,
              fontSize: token.fontSizeSM,
            }}
          >
            ↑ ↓ {t('选择', 'select')} · Enter {t('打开', 'open')} · Esc{' '}
            {t('关闭', 'close')}
          </div>
        )
      }
      onCancel={onClose}
      width={560}
      style={{ top: 'min(100px, 4dvh)', paddingBottom: 16 }}
      destroyOnHidden
      afterOpenChange={(shown) => {
        if (shown) input.current?.focus();
      }}
    >
      <Input
        ref={input}
        allowClear
        prefix={<SearchOutlined />}
        value={query}
        placeholder={t('输入菜单名称或路径…', 'Type a menu name or path…')}
        aria-label={t('搜索菜单', 'Search menus')}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={results[active] ? `${listId}-${active}` : undefined}
        onCompositionStart={() => {
          composing.current = true;
        }}
        onCompositionEnd={() => {
          composing.current = false;
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setActive(0);
        }}
        onKeyDown={(event) => {
          if (
            composing.current ||
            event.nativeEvent.isComposing ||
            event.keyCode === 229
          ) {
            event.stopPropagation();
            return;
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            onClose();
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            choose(active);
          }
          if (results.length && (event.key === 'Home' || event.key === 'End')) {
            event.preventDefault();
            scrollSelection.current = true;
            setActive(event.key === 'Home' ? 0 : results.length - 1);
          }
          if (
            results.length &&
            (event.key === 'ArrowDown' || event.key === 'ArrowUp')
          ) {
            event.preventDefault();
            scrollSelection.current = true;
            setActive(
              (index) =>
                (index + (event.key === 'ArrowDown' ? 1 : -1) + results.length) %
                results.length,
            );
          }
        }}
      />
      <div
        ref={list}
        id={listId}
        role="listbox"
        aria-label={t('搜索结果', 'Search results')}
        style={{
          maxHeight: 'min(360px, max(64px, calc(100dvh - 200px)))',
          overflowY: 'auto',
          marginTop: token.marginSM,
        }}
      >
        {results.map((item, index) => (
          <button
            key={item.key}
            id={`${listId}-${index}`}
            type="button"
            role="option"
            aria-selected={index === active}
            tabIndex={-1}
            onClick={() => choose(index)}
            onMouseMove={(event) => {
              const previous = pointerPosition.current;
              if (previous?.x === event.clientX && previous.y === event.clientY) return;
              pointerPosition.current = { x: event.clientX, y: event.clientY };
              scrollSelection.current = false;
              setActive(index);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: isPhone ? 'wrap' : undefined,
              gap: token.marginSM,
              width: '100%',
              border: 0,
              borderRadius: token.borderRadius,
              padding: token.paddingSM,
              cursor: 'pointer',
              textAlign: 'start',
              background: index === active ? token.controlItemBgHover : 'transparent',
              color: token.colorText,
            }}
          >
            {item.icon}
            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
              {localizeMenuItemName(item, locale)}
            </span>
            <small
              title={item.path}
              style={{
                marginInlineStart: 'auto',
                flexBasis: isPhone ? '100%' : undefined,
                color: token.colorTextSecondary,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {item.path}
            </small>
          </button>
        ))}
      </div>
      {!results.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('没有匹配菜单', 'No matching menus')}
        />
      )}
    </Modal>
  );
}
